import { BASE_URL } from 'config';
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./wallets.css";
import Grid from "@mui/material/Grid";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import Box from "@mui/material/Box";
import MDTypography from "components/MDTypography";
import { styled } from "@mui/material/styles";
import DataTable from "examples/Tables/DataTable";
import allWalletsTableData from "./data/allWalletsTableData";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import TextField from "@mui/material/TextField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

const GradientBackground = styled("div")({
  minHeight: "100vh",
  width: "100%",
  background: "linear-gradient(135deg, #3a2377 0%, #5f259f 100%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingBottom: "40px",
});

const getCardGradient = (type) => {
  switch (type) {
    case "Customer":
      return "linear-gradient(135deg, #1de9b6 0%, #43e97b 60%, #38f9d7 100%)";
    case "Admin":
      return "linear-gradient(135deg, #6a5af9 0%, #7b2ff2 60%, #5f72bd 100%)";
    case "Store":
      return "linear-gradient(135deg, #ffe259 0%, #ffa751 100%)";
    default:
      return "linear-gradient(135deg, #64748b 0%, #a1a1aa 100%)";
  }
};

const WalletCard = styled(Box)(({ gradient }) => ({
  background: gradient,
  borderRadius: 24,
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.25)",
  color: "#fff",
  minWidth: 280,
  maxWidth: 320,
  minHeight: 240,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "transform 0.3s, box-shadow 0.3s",
  cursor: "pointer",
  willChange: "transform",
  "&:hover": {
    transform: "scale(1.045) rotateY(-2deg)",
    boxShadow: "0 16px 40px 0 rgba(31, 38, 135, 0.35)",
  },
}));

const StatusBadge = styled("span")(({ active }) => ({
  background: active ? "#bbf7d0" : "#fecaca",
  color: active ? "#166534" : "#991b1b",
  fontWeight: 600,
  borderRadius: 16,
  padding: "4px 16px",
  fontSize: 15,
  position: "absolute",
  top: 18,
  right: 18,
  zIndex: 2,
  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
}));

const wallets = [
  {
    id: 1,
    name: "عميل",
    balance: "$1,200.50",
    type: "عميل",
  },
  {
    id: 3,
    name: "مالك المتجر",
    balance: "$2,450.75",
    type: "متجر",
  },
];

function Wallets() {
  const [walletsData, setWalletsData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWallets = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("user-token");
        const response = await fetch(
          BASE_URL + "/administrator/wallets/all",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        setWalletsData(data.data || []);
      } catch (error) {
        setWalletsData([]);
      }
      setLoading(false);
    };
    fetchWallets();
  }, []);
  const { columns, rows } = allWalletsTableData(walletsData);

  const filteredRows = rows.filter((row) => row.id.toString().includes(search));

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setPage(0); // تصفير الصفحة عند تغيير البحث
  }, [search]);
  return (
    <>
    <Backdrop open={loading} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="wallets-container">
        {/* Wallet Cards */}
        <main className="wallets-main">
          <div className="wallets-grid">
            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className={`wallet-card wallet-card-${
                  wallet.type === "عميل" ? "customer" : wallet.type === "مشرف" ? "admin" : "store"
                }`}
                onClick={() => {
                  if (wallet.type === "عميل") navigate("/customer_wallets");
                  if (wallet.type === "مشرف") navigate(`/operations/${wallet.id}`);
                  if (wallet.type === "متجر") navigate("/store_wallets");
                }}
                style={{ cursor: wallet.type === "عميل" ? "pointer" : "default" }}
              >
                {/* Wallet Icon */}
                <span className="wallet-icon">
                  <WalletIcon
                    style={{
                      fontSize: 38,
                      color: "#fff",
                      opacity: 0.92,
                      filter: "drop-shadow(0 2px 8px #0002)",
                    }}
                  />
                </span>
                {/* Wallet Details */}
                <h2 className="wallet-name">{wallet.name}</h2>
                <p className="wallet-balance">الرصيد: {wallet.balance}</p>
                <p className="wallet-detail">النوع: {wallet.type}</p>
              </div>
            ))}
          </div>
          {/* Wallets DataTable */}
          <MDBox pt={3} px={2}>
            <TextField
              fullWidth
              label=" بحث برقم المحفظة"
              variant="outlined"
              size="small"
              sx={{ mb: 3 }}
              onChange={(e) => setSearch(e.target.value)}
            />
            <DataTable
              table={{ columns, rows: paginatedRows }}
              isSorted={false}
              entriesPerPage={false} // نعطل تقسيم الصفحات الداخلي
              showTotalEntries={true}
              noEndBorder
              sx={{
                "& .MuiTable-root": { minWidth: 1000 },
                "& .MuiTableCell-root": {
                  py: 1.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                },
                "& .MuiTableHead-root": {
                  backgroundColor: (theme) => theme.palette.grey[100],
                },
              }}
            />

            {/* أزرار التحكم في الصفحات */}
            <MDBox display="flex" justifyContent="center" mt={4} pb={4} px={2}>
              <MDButton
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                disabled={page === 0}
                sx={{
                  background: "linear-gradient(90deg, #ca9937, #e0b15a)",
                  color: "white",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  minWidth: "50px",
                  mx: 1,
                  "&:hover": {
                    background: "linear-gradient(90deg, #b6842f, #d39d45)",
                  },
                }}
              >
                <ArrowBackIcon />
              </MDButton>

              <MDTypography
                variant="button"
                color="text"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mx: 3,
                  fontWeight: "bold",
                }}
              >
                الصفحة {page + 1} من {Math.ceil(filteredRows.length / rowsPerPage)}
              </MDTypography>

              <MDButton
                onClick={() =>
                  setPage((prev) =>
                    (prev + 1) * rowsPerPage >= filteredRows.length ? prev : prev + 1
                  )
                }
                disabled={(page + 1) * rowsPerPage >= filteredRows.length}
                sx={{
                  background: "linear-gradient(90deg, #ca9937, #e0b15a)",
                  color: "white",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  minWidth: "50px",
                  mx: 1,
                  "&:hover": {
                    background: "linear-gradient(90deg, #b6842f, #d39d45)",
                  },
                }}
              >
                <ArrowForwardIcon />
              </MDButton>
            </MDBox>
          </MDBox>
        </main>
      </div>
    
      </>
  );
}

export default Wallets;
